import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import type {
  DesktopReleaseCreateRequest,
  DesktopReleaseItem,
  DesktopReleaseUploadQuery,
} from '@cms/models';
import { desktopReleasesApi, useUpdateDesktopRelease } from '@/lib/api/desktop-releases.api';
import { sha256FileHex } from '@/lib/upload/presigned-put';
import { cn } from '@/lib/utils';
import { CommonModal } from '@/components/common/CommonModal';
import { Toggle } from '@/components/ui/toggle';

type DesktopReleaseFormData = {
  appSlug: string;
  channel: 'stable' | 'beta';
  platform: 'win32' | 'darwin' | 'linux';
  arch: 'x64' | 'arm64' | '';
  version: string;
  releaseNotes: string;
  isMandatory: boolean;
  status: 'active' | 'inactive';
  killList: string;
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20';
const textareaClass =
  'mt-1 block w-full px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20';

export type DesktopReleaseFormModalMode = 'create' | 'view' | 'edit';

type DesktopReleaseFormModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly mode: DesktopReleaseFormModalMode;
  readonly release: DesktopReleaseItem | null;
  readonly defaultAppSlug?: string;
};

export function DesktopReleaseFormModal({
  open,
  onClose,
  onSuccess,
  mode,
  release,
  defaultAppSlug = 'veo3-pro',
}: DesktopReleaseFormModalProps) {
  const formId = 'desktop-release-form';
  const isCreate = mode === 'create';
  const isView = mode === 'view';
  const updateMutation = useUpdateDesktopRelease();
  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<DesktopReleaseFormData>({
    defaultValues: {
      appSlug: defaultAppSlug,
      channel: 'stable',
      platform: 'win32',
      arch: '',
      version: '',
      releaseNotes: '',
      isMandatory: false,
      status: 'active',
      killList: '',
    },
  });
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      reset();
      setFile(null);
      setUploadProgress(null);
      return;
    }
    if (release) {
      reset({
        appSlug: release.appSlug,
        channel: release.channel,
        platform: release.platform,
        arch: (release.arch ?? '') as 'x64' | 'arm64' | '',
        version: release.version,
        releaseNotes: release.releaseNotes ?? '',
        isMandatory: release.isMandatory,
        status: release.status ?? 'inactive',
        killList: release.killList ? release.killList.join(', ') : '',
      });
      return;
    }
    reset({
      appSlug: defaultAppSlug,
      channel: 'stable',
      platform: 'win32',
      arch: '',
      version: '',
      releaseNotes: '',
      isMandatory: false,
      status: 'active',
      killList: '',
    });
    setFile(null);
  }, [open, release, defaultAppSlug, reset]);

  const onSubmit = async (data: DesktopReleaseFormData) => {
    try {
      if (isView) return;
      if (!isCreate) {
        if (!release?.id) {
          toast.error('Missing release id');
          return;
        }
        const payload: {
          releaseNotes?: string;
          isMandatory?: boolean;
          objectKey?: string;
          fileName?: string;
          sizeBytes?: number;
          sha256Hex?: string;
          killList?: string[];
        } = {
          releaseNotes: data.releaseNotes.trim() || '',
          isMandatory: data.isMandatory,
          killList: data.killList.split(',').map(s => s.trim()).filter(Boolean),
        };
        if (file) {
          const uploadQuery: DesktopReleaseUploadQuery = {
            appSlug: data.appSlug.trim(),
            channel: data.channel,
            version: data.version.trim(),
            platform: data.platform,
            arch: data.arch || undefined,
            fileName: file.name,
          };
          setUploadProgress(0);
          const upload = await desktopReleasesApi.uploadArtifact(uploadQuery, file, setUploadProgress);
          payload.objectKey = upload.objectKey;
          payload.fileName = file.name;
          payload.sizeBytes = file.size;
          payload.sha256Hex = await sha256FileHex(file);
          setUploadProgress(null);
        }
        await updateMutation.mutateAsync({ releaseId: release.id, payload });

        // Sync active/inactive status via publish/unpublish.
        if (release.status !== data.status) {
          if (data.status === 'active') {
            await desktopReleasesApi.publishRelease(release.id);
          } else {
            await desktopReleasesApi.unpublishRelease(release.id);
          }
        }
        toast.success('Desktop release updated successfully');
        onSuccess();
        onClose();
        return;
      }

      if (!file) {
        toast.error('Please select installer file');
        return;
      }
      if (!data.version.trim()) {
        toast.error('Version is required');
        return;
      }

      const uploadQuery: DesktopReleaseUploadQuery = {
        appSlug: data.appSlug.trim(),
        channel: data.channel,
        version: data.version.trim(),
        platform: data.platform,
        arch: data.arch || undefined,
        fileName: file.name,
      };

      setUploadProgress(0);
      const upload = await desktopReleasesApi.uploadArtifact(uploadQuery, file, setUploadProgress);
      const sha256Hex = await sha256FileHex(file);
      setUploadProgress(null);

      const createPayload: DesktopReleaseCreateRequest = {
        appSlug: uploadQuery.appSlug,
        channel: uploadQuery.channel,
        version: uploadQuery.version,
        platform: uploadQuery.platform,
        arch: uploadQuery.arch ?? undefined,
        objectKey: upload.objectKey,
        fileName: uploadQuery.fileName,
        sizeBytes: file.size,
        sha256Hex,
        releaseNotes: data.releaseNotes.trim() || undefined,
        isMandatory: data.isMandatory,
        killList: data.killList.split(',').map(s => s.trim()).filter(Boolean),
      };

      const created = await desktopReleasesApi.createRelease(createPayload);
      if (!created.id) {
        throw new Error('Missing created release id');
      }
      if (data.status === 'active') {
        await desktopReleasesApi.publishRelease(created.id);
      }

      toast.success('Desktop release uploaded successfully');
      onSuccess();
      onClose();
    } catch (error: unknown) {
      setUploadProgress(null);
      toast.error(extractApiErrorMessage(error));
    }
  };

  const title =
    mode === 'create'
      ? 'Create desktop release'
      : mode === 'view'
        ? 'Desktop release details'
        : 'Edit desktop release';

  const isReadOnlyFields = !isCreate;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      panelClassName="max-w-2xl"
      overlayClassName="p-0"
      footer={
        isView ? (
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button form={formId} type="submit" disabled={isSubmitting || updateMutation.isPending}>
              {isCreate
                ? isSubmitting
                  ? 'Uploading…'
                  : 'Upload & create'
                : updateMutation.isPending
                  ? 'Saving…'
                  : 'Save'}
            </Button>
          </>
        )
      }
      footerClassName="flex justify-end gap-2"
    >
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label
              htmlFor="desktop-release-app-slug"
              className="block text-sm font-medium text-foreground"
            >
              App slug
            </label>
            <input
              id="desktop-release-app-slug"
              disabled={isReadOnlyFields}
              {...register('appSlug')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            />
          </div>
          <div>
            <label
              htmlFor="desktop-release-channel"
              className="block text-sm font-medium text-foreground"
            >
              Channel
            </label>
            <select
              id="desktop-release-channel"
              disabled={isReadOnlyFields}
              {...register('channel')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            >
              <option value="stable">stable</option>
              <option value="beta">beta</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="desktop-release-platform"
              className="block text-sm font-medium text-foreground"
            >
              Platform
            </label>
            <select
              id="desktop-release-platform"
              disabled={isReadOnlyFields}
              {...register('platform')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            >
              <option value="win32">win32</option>
              <option value="darwin">darwin</option>
              <option value="linux">linux</option>
            </select>
          </div>
          <div>
            <label
              htmlFor="desktop-release-arch"
              className="block text-sm font-medium text-foreground"
            >
              Arch (optional)
            </label>
            <select
              id="desktop-release-arch"
              disabled={isReadOnlyFields}
              {...register('arch')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            >
              <option value="">(any)</option>
              <option value="x64">x64</option>
              <option value="arm64">arm64</option>
            </select>
          </div>

          <div>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <div className="space-y-1 mt-2">
                  <Toggle
                    id="desktop-release-status"
                    label="Status"
                    checked={field.value === 'active'}
                    disabled={isView}
                    onCheckedChange={checked => field.onChange(checked ? 'active' : 'inactive')}
                  />
                  <div className="text-sm text-muted-foreground">
                    {field.value === 'active' ? 'Active' : 'Inactive'}
                  </div>
                </div>
              )}
            />
          </div>

          <div className="sm:col-span-2">
            <label
              htmlFor="desktop-release-version"
              className="block text-sm font-medium text-foreground"
            >
              Version
            </label>
            <input
              id="desktop-release-version"
              placeholder="e.g. 1.2.3"
              disabled={isReadOnlyFields}
              {...register('version')}
              className={cn(inputClass, isReadOnlyFields && 'cursor-default bg-muted/50')}
            />
          </div>

          <div className="sm:col-span-2">
            <label
              htmlFor="desktop-release-notes"
              className="block text-sm font-medium text-foreground"
            >
              Release notes (optional)
            </label>
            <textarea
              id="desktop-release-notes"
              rows={3}
              disabled={isView}
              {...register('releaseNotes')}
              className={cn(textareaClass, isView && 'cursor-default bg-muted/50')}
            />
          </div>

          <div className="sm:col-span-2">
            <label
              htmlFor="desktop-release-kill-list"
              className="block text-sm font-medium text-foreground"
            >
              Kill list (comma separated process names)
            </label>
            <textarea
              id="desktop-release-kill-list"
              rows={2}
              placeholder="e.g. chrome.exe, veo3-helper"
              disabled={isView}
              {...register('killList')}
              className={cn(textareaClass, isView && 'cursor-default bg-muted/50')}
            />
          </div>

          <div>
            <label
              htmlFor="desktop-release-mandatory"
              className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground"
            >
              <input
                id="desktop-release-mandatory"
                type="checkbox"
                disabled={isView}
                {...register('isMandatory')}
              />
              <span>Mandatory update</span>
              <span className="text-muted-foreground">{watch('isMandatory') ? 'Yes' : 'No'}</span>
            </label>
          </div>

          {(isCreate || mode === 'edit') && (
            <div className="sm:col-span-2">
              <label
                htmlFor="desktop-release-file"
                className="block text-sm font-medium text-foreground"
              >
                {isCreate ? 'Installer file' : 'Replace installer file (optional)'}
              </label>
              <input
                id="desktop-release-file"
                type="file"
                disabled={isView}
                onChange={e => setFile(e.target.files?.[0] ?? null)}
                className="mt-1 block w-full text-sm text-muted-foreground"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {file
                  ? `Selected: ${file.name} (${Math.round(file.size / 1024 / 1024)} MB)`
                  : 'No file selected'}
              </p>
              {uploadProgress != null && (
                <div className="mt-2 space-y-1">
                  <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary transition-[width] duration-150"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">Uploading… {uploadProgress}%</p>
                </div>
              )}
            </div>
          )}
        </div>
      </form>
    </CommonModal>
  );
}
